import {
  AddIcon,
  RemoveIcon,
  DeleteOutlineIcon,
  EditOutlinedIcon,
  RestaurantIcon,
} from "./icons";
import toPriceDisplay from "../utils/toPriceDisplay";
import qttDisplay from "../utils/qttDisplay";
import UnavailableDeliveryWarning from "./UnavailableDeliveryWarning";

/**
 * Itens da sacola: foto, nome, escolhas, preço e o ajuste de quantidade no
 * próprio item. Tocar na foto, no lápis ou na descrição abre a edição; no "−"
 * da quantidade mínima, o botão vira lixeira (é assim que se remove).
 */
export default function ProductsListCart(props) {
  const { products, onEdit, onRemove, onIncrement, onDecrement } = props;

  return (
    <div className="cart-list">
      <ul className="cart-list__items">
        {products.map((product) => (
          <li key={product.uuid}>
            <CartItem
              product={product}
              onEdit={() => onEdit(product)}
              onRemove={() => onRemove(product)}
              onIncrement={() => onIncrement(product)}
              onDecrement={() => onDecrement(product)}
            />
          </li>
        ))}
      </ul>
      <UnavailableDeliveryWarning />
    </div>
  );
}

function CartItem({ product, onEdit, onRemove, onIncrement, onDecrement }) {
  const { error } = product;
  const hasError = error.isDisabled || error.isPriceChanged;
  const atMinimum = product.qtt <= product.qttMinimum;
  // Item indisponível não abre a edição: só dá para tirá-lo (lixeira).
  const canEdit = !error.isDisabled;

  // Foto, lápis e descrição levam à edição. Preço e quantidade ficam fora
  // dessa área, para o +/− não abrir o produto.
  const editProps = canEdit
    ? {
        role: "button",
        tabIndex: 0,
        onClick: onEdit,
        onKeyDown: (event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onEdit();
          }
        },
      }
    : {};

  return (
    <div className={`cart-item${hasError ? " has-error" : ""}`}>
      <div
        className={`cart-item__media${canEdit ? " is-editable" : ""}`}
        onClick={canEdit ? onEdit : undefined}
      >
        {product.pictureUrl ? (
          <img
            src={product.pictureUrl}
            alt=""
            loading="lazy"
            decoding="async"
            width="56"
            height="56"
            className="cart-item__img"
          />
        ) : (
          <span className="cart-item__placeholder" aria-hidden="true">
            <RestaurantIcon fontSize="small" />
          </span>
        )}
        {canEdit && (
          <button
            type="button"
            className="cart-item__edit"
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            aria-label={`Editar ${product.productName}`}
          >
            <EditOutlinedIcon fontSize="inherit" />
          </button>
        )}
      </div>
      <div className="cart-item__body">
        <div
          className={`cart-item__info${canEdit ? " is-editable" : ""}`}
          {...editProps}
        >
          <p className="cart-item__name">
            {product.productName}
            {error.isDisabled && (
              <span className="tag tag--muted cart-item__tag">
                Indisponível
              </span>
            )}
          </p>
          {product.detailsList && product.detailsList.length > 0 && (
            <ul className="cart-item__details">
              {product.detailsList.map((detail, index) => (
                <li key={index}>
                  {detail.name}
                  {detail.error.isDisabled && (
                    <span className="tag tag--muted cart-item__tag">
                      Indisponível
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="cart-item__footer">
          <div className="cart-item__price">
            <span>{toPriceDisplay(product.price)}</span>
            {error.isPriceChanged && (
              <span className="cart-item__price-diff">
                {`preço ${error.priceDiff.gt(0) ? "subiu" : "caiu"} ${toPriceDisplay(
                  error.priceDiff.abs(),
                )}`}
              </span>
            )}
          </div>
          <div className="qty-stepper qty-stepper--sm">
            <button
              type="button"
              className="icon-button"
              onClick={atMinimum ? onRemove : onDecrement}
              aria-label={
                atMinimum
                  ? `Remover ${product.productName}`
                  : `Diminuir ${product.productName}`
              }
            >
              {atMinimum ? (
                <DeleteOutlineIcon fontSize="small" />
              ) : (
                <RemoveIcon fontSize="small" />
              )}
            </button>
            <span className="qty-stepper__value">
              {qttDisplay(product.qtt, product.isKilogram)}
            </span>
            <button
              type="button"
              className="icon-button"
              onClick={onIncrement}
              disabled={error.isDisabled}
              aria-label={`Aumentar ${product.productName}`}
            >
              <AddIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
